/**
 * Gallery card (snippets/gallery-card.liquid)
 *  - <gallery-card-slider>: swipeable, looping image gallery with dynamic dots
 *  - <wishlist-toggle>: wishlist button with optional size picker (localStorage)
 * Native browser APIs only. Safe to load more than once.
 *
 * Wishlist API for other components:
 *   window.themeWishlist.get()            -> [{ productId, variantId, url }]
 *   window.themeWishlist.find(productId)  -> item | undefined
 *   window.themeWishlist.add(item) / remove(productId)
 *   document.addEventListener('wishlist:change', (e) => e.detail.items)
 */

(() => {
const GALLERY_CARD_MOBILE_QUERY = '(max-width: 749px)';

if (!customElements.get('gallery-card-slider')) {
  class GalleryCardSlider extends HTMLElement {
    /** @type {HTMLElement | null} */
    #track = null;
    /** @type {HTMLElement[]} */
    #slides = [];
    /** @type {HTMLButtonElement[]} */
    #dots = [];
    #index = 0;
    #frame = 0;
    #dragStartX = null;
    #dragged = false;
    #controller = new AbortController();

    connectedCallback() {
      this.#track = this.querySelector('[data-gallery-track]');
      this.#slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      if (!this.#track || this.#slides.length < 2) return;

      const { signal } = this.#controller;
      this.#buildPagination();
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', (event) => this.#onArrow(event, -1), { signal });
      this.querySelector('[data-gallery-next]')?.addEventListener('click', (event) => this.#onArrow(event, 1), { signal });
      this.#track.addEventListener('scroll', this.#onScroll.bind(this), { passive: true, signal });
      this.#track.addEventListener('pointerdown', this.#onPointerDown.bind(this), { signal });
      this.#track.addEventListener('click', this.#onTrackClick.bind(this), { capture: true, signal });
      this.#track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      window.addEventListener('pointerup', this.#onPointerUp.bind(this), { signal });
      // Slides 2+ are rendered with data-src: load them once the shopper starts browsing this card
      const load = () => this.#loadSlides();
      for (const type of ['pointerenter', 'touchstart', 'focusin']) this.addEventListener(type, load, { passive: true, signal });
      this.#track.addEventListener('scroll', load, { passive: true, signal });
      this.#update();
    }

    #slidesLoaded = false;

    /** Swaps data-src/data-srcset into the off-screen slide images (not for single-image mobile cards). */
    #loadSlides() {
      if (this.#slidesLoaded || this.#isStatic()) return;
      this.#slidesLoaded = true;
      for (const image of this.querySelectorAll('img[data-src]')) {
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
      }
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#controller = new AbortController();
      this.#dots = [];
      cancelAnimationFrame(this.#frame);
    }

    /**
     * Go to a slide. Loops at both ends.
     * @param {number} index
     * @param {boolean} [announce]
     */
    goTo(index, announce = false) {
      if (this.#isStatic()) return;
      this.#loadSlides();
      const total = this.#slides.length;
      const target = ((index % total) + total) % total;
      const width = this.#track.clientWidth;
      const left = this.#isRtl() ? -width * target : width * target;

      this.#track.scrollTo({ left, behavior: this.#reducedMotion() ? 'auto' : 'smooth' });
      this.#index = target;
      this.#update(announce);
    }

    /** Single image mode on mobile (card has gallery-card--static-mobile). */
    #isStatic() {
      return Boolean(this.closest('.gallery-card--static-mobile')) && window.matchMedia(GALLERY_CARD_MOBILE_QUERY).matches;
    }

    #isRtl() {
      return getComputedStyle(this.#track).direction === 'rtl';
    }

    #reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    #buildPagination() {
      const pagination = this.querySelector('[data-gallery-pagination]');
      if (!pagination) return;

      pagination.replaceChildren();
      const template = this.dataset.labelGoTo || '';

      for (const [index] of this.#slides.entries()) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'gallery-card__dot';
        dot.setAttribute('aria-label', template.replace('[index]', String(index + 1)));
        dot.setAttribute('aria-controls', this.#track.id);
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.goTo(index, true);
        }, { signal: this.#controller.signal });
        pagination.appendChild(dot);
        this.#dots.push(dot);
      }
    }

    /**
     * @param {MouseEvent} event
     * @param {number} direction
     */
    #onArrow(event, direction) {
      event.preventDefault();
      event.stopPropagation();
      this.goTo(this.#index + direction, true);
    }

    #onScroll() {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        const width = this.#track.clientWidth || 1;
        const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
        const clamped = Math.min(Math.max(index, 0), this.#slides.length - 1);
        if (clamped === this.#index) return;
        this.#index = clamped;
        this.#update();
      });
    }

    /** Mouse drag (touch devices use native scroll snapping). */
    #onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      // A parent carousel can own mouse dragging (arrows and dots still work).
      if (this.closest('[data-disable-card-drag]')) return;
      this.#dragStartX = event.clientX;
      this.#dragged = false;
    }

    #onPointerUp(event) {
      if (this.#dragStartX === null) return;
      const delta = event.clientX - this.#dragStartX;
      this.#dragStartX = null;
      if (Math.abs(delta) < 40) return;

      this.#dragged = true;
      const direction = delta < 0 ? 1 : -1;
      this.goTo(this.#index + (this.#isRtl() ? -direction : direction));
    }

    /** Don't open the product right after a mouse drag. */
    #onTrackClick(event) {
      if (!this.#dragged) return;
      event.preventDefault();
      event.stopPropagation();
      this.#dragged = false;
    }

    /** @param {boolean} [announce] */
    #update(announce = false) {
      const total = this.#slides.length;
      const visibleDots = 5;
      const half = Math.floor(visibleDots / 2);
      let start = Math.max(0, this.#index - half);
      const end = Math.min(total - 1, start + visibleDots - 1);
      start = Math.max(0, end - visibleDots + 1);

      for (const [index, slide] of this.#slides.entries()) {
        slide.toggleAttribute('inert', index !== this.#index);
      }

      for (const [index, dot] of this.#dots.entries()) {
        const isActive = index === this.#index;
        dot.classList.toggle('gallery-card__dot--active', isActive);
        dot.classList.toggle('gallery-card__dot--near', Math.abs(index - this.#index) === 1);
        dot.toggleAttribute('hidden', index < start || index > end);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      }

      if (!announce) return;
      const status = this.querySelector('[data-gallery-status]');
      if (!status) return;
      const message = (this.dataset.labelSlide || '')
        .replace('[index]', String(this.#index + 1))
        .replace('[count]', String(total));
      status.textContent = '';
      requestAnimationFrame(() => {
        status.textContent = message;
      });
    }
  }

  customElements.define('gallery-card-slider', GalleryCardSlider);
}

if (!window.themeWishlist) {
  const STORAGE_KEY = 'theme:wishlist';

  /**
   * @typedef {Object} WishlistItem
   * @property {string} productId
   * @property {string} variantId
   * @property {string} url - product URL when saved (may include a locale prefix)
   * @property {string} [handle] - product handle (used to build URLs in the current locale)
   * @property {number} [addedAt] - timestamp (ms)
   */

  /** @param {string} url */
  const handleFromUrl = (url = '') => (url.split('/products/')[1] || '').split(/[?#/]/)[0];

  /** @param {any} item @returns {WishlistItem | null} */
  const normalize = (item) => {
    if (!item || !item.productId) return null;
    return {
      productId: String(item.productId),
      variantId: String(item.variantId || ''),
      url: item.url || '',
      handle: item.handle || handleFromUrl(item.url),
      addedAt: Number(item.addedAt) || Date.now()
    };
  };

  /** @returns {WishlistItem[]} */
  const read = () => {
    try {
      const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(items) ? items.map(normalize).filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  /** @param {WishlistItem[]} items @param {string} [source] */
  const write = (items, source = 'local') => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Storage blocked (private mode) - state lives for this page only.
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items, source } }));
  };

  window.themeWishlist = {
    get: read,
    /** @param {string} productId */
    find: (productId) => read().find((item) => item.productId === String(productId)),
    /** @param {WishlistItem} item */
    add(item) {
      const items = read().filter((entry) => entry.productId !== String(item.productId));
      const next = normalize({ ...item, addedAt: Date.now() });
      if (next) items.push(next);
      write(items);
    },
    /** @param {string} productId */
    remove(productId) {
      write(read().filter((item) => item.productId !== String(productId)));
    },
    /**
     * Union with another list (e.g. the customer's saved account wishlist). Local entries win.
     * @param {WishlistItem[]} incoming
     * @param {string} [source]
     */
    merge(incoming, source = 'account') {
      const items = read();
      const known = new Set(items.map((item) => item.productId));
      let changed = false;
      for (const entry of incoming || []) {
        const item = normalize(entry);
        if (!item || known.has(item.productId)) continue;
        items.push(item);
        known.add(item.productId);
        changed = true;
      }
      if (changed) write(items, source);
      return items;
    }
  };
}

if (!customElements.get('wishlist-toggle')) {
  /** Shared size picker dialog. */
  const WishlistDialog = {
    /** @type {HTMLDialogElement | null} */
    element: null,
    /** @type {HTMLElement | null} */
    trigger: null,

    get() {
      if (this.element) return this.element;

      const dialog = document.createElement('dialog');
      dialog.className = 'gallery-card-dialog';
      dialog.setAttribute('aria-labelledby', 'GalleryCardDialogTitle');
      dialog.innerHTML = `
        <form method="dialog" class="gallery-card-dialog__form">
          <div class="gallery-card-dialog__header">
            <h2 class="gallery-card-dialog__title" id="GalleryCardDialogTitle"></h2>
            <button type="button" class="gallery-card-dialog__close" data-dialog-close><span aria-hidden="true">&times;</span></button>
          </div>
          <fieldset class="gallery-card-dialog__options" data-dialog-options>
            <legend class="visually-hidden" data-dialog-legend></legend>
          </fieldset>
          <button type="submit" class="button gallery-card-dialog__submit" data-dialog-submit></button>
          <p class="gallery-card-dialog__status" role="status" aria-live="polite" data-dialog-status></p>
        </form>`;
      document.body.appendChild(dialog);

      dialog.querySelector('[data-dialog-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('close', () => this.trigger?.focus());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });

      this.element = dialog;
      return dialog;
    }
  };

  class WishlistToggle extends HTMLElement {
    #controller = new AbortController();

    connectedCallback() {
      const { signal } = this.#controller;
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', this.#onClick.bind(this), { signal });
      document.addEventListener('wishlist:change', this.#sync.bind(this), { signal });
      window.addEventListener('storage', this.#sync.bind(this), { signal });
      this.#sync();
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#controller = new AbortController();
    }

    #sync() {
      if (!this.button) return;
      const saved = Boolean(window.themeWishlist.find(this.dataset.productId));
      this.button.setAttribute('aria-pressed', String(saved));
      const label = saved ? this.dataset.labelRemove : this.dataset.labelSave;
      if (label) this.button.setAttribute('aria-label', label);
    }

    /** @param {MouseEvent} event */
    #onClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.hasAttribute('data-pick-size') && typeof HTMLDialogElement === 'function') {
        this.#openPicker();
        return;
      }
      this.#toggle();
    }

    #toggle() {
      const { productId, variantId, productUrl } = this.dataset;
      if (window.themeWishlist.find(productId)) {
        window.themeWishlist.remove(productId);
        return;
      }
      window.themeWishlist.add({ productId, variantId, url: productUrl });
    }

    async #openPicker() {
      const { dataset } = this;
      const dialog = WishlistDialog.get();
      const form = dialog.querySelector('form');
      const options = dialog.querySelector('[data-dialog-options]');
      const submit = dialog.querySelector('[data-dialog-submit]');
      const status = dialog.querySelector('[data-dialog-status]');
      const saved = window.themeWishlist.find(dataset.productId);

      let product;
      try {
        const url = new URL(`${dataset.productUrl.split('?')[0]}.js`, window.location.origin);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        product = await response.json();
      } catch (error) {
        console.error('Wishlist: could not load product', error);
        this.#toggle();
        return;
      }

      WishlistDialog.trigger = this.button;
      dialog.querySelector('#GalleryCardDialogTitle').textContent = dataset.pickLabel || '';
      dialog.querySelector('[data-dialog-close]').setAttribute('aria-label', dataset.pickClose || '');
      dialog.querySelector('[data-dialog-legend]').textContent = dataset.productTitle || '';
      submit.textContent = saved ? dataset.pickRemove : dataset.pickSubmit;
      submit.dataset.mode = saved ? 'remove' : 'add';
      status.textContent = '';
      for (const option of options.querySelectorAll('.gallery-card-dialog__option')) option.remove();

      for (const variant of product.variants) {
        const label = document.createElement('label');
        label.className = 'gallery-card-dialog__option';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'wishlist-variant';
        input.value = String(variant.id);
        input.required = true;
        input.checked = String(variant.id) === (saved ? saved.variantId : dataset.variantId);
        const text = document.createElement('span');
        text.textContent = variant.available ? variant.title : `${variant.title} - ${dataset.pickUnavailable || ''}`;
        label.append(input, text);
        options.appendChild(label);
      }

      form.onsubmit = (event) => {
        event.preventDefault();
        if (submit.dataset.mode === 'remove') {
          window.themeWishlist.remove(dataset.productId);
          dialog.close();
          return;
        }
        const selected = form.querySelector('input[name="wishlist-variant"]:checked');
        if (!selected) return;
        window.themeWishlist.add({ productId: dataset.productId, variantId: selected.value, url: dataset.productUrl });
        status.textContent = dataset.pickSaved || '';
        setTimeout(() => {
          if (dialog.open) dialog.close();
        }, 900);
      };

      dialog.showModal();
      (options.querySelector('input:checked') || options.querySelector('input'))?.focus();
    }
  }

  customElements.define('wishlist-toggle', WishlistToggle);
}
})();
